import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEPLOYMENT_KIND_LABELS,
  DeploymentKind,
  DeploymentReason,
  ROBOT_STATUS_LABELS,
  getHolidayName,
  isKoreanHoliday,
  robotStatusForDeploymentKind,
  type OwnerEntity,
  type StoreSummaryDto,
} from '@bhops/shared';
import { deploymentsApi } from '@/api/deployments';
import { robotsApi } from '@/api/robots';
import { storesApi } from '@/api/stores';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { StorePicker } from '@/components/StorePicker';
import { CustomerPicker } from '@/components/pickers/CustomerPicker';
import { useToast } from '@/components/Toast';
import { canCreateStore } from '@/permissions';
import { useAuthStore } from '@/stores/auth';
import { CONTRACT_REASON_OPTION_LABEL } from '@/lib/contract-reason';
import { kstTodayYmd, ymdToIso } from '@/lib/format-date';
import { customerPickerEntityForRobot, robotUsableAt, robotUseDeniedMessage } from './robot-entity';

interface Props {
  open: boolean;
  onClose: () => void;
  robotId: string;
}

// round11 요구4 — 고객 배치(유형)와 어울리지 않는 사유들. 이 조합이면 사용자가
// '유형'과 '사유'를 혼동한 것이므로 안내를 띄운다.
const NON_CUSTOMER_REASONS: DeploymentReason[] = [
  DeploymentReason.EVENT,
  DeploymentReason.DEMO,
  DeploymentReason.PILOT,
  DeploymentReason.INTERNAL,
];

// 계약 사실화 1단계 정정4(26-07-29) — '사유' 드롭다운의 **미지정** 값.
//
// 종전 이 창은 사유를 SALE 로 잡아 두고 mutation body 에 늘 넣어 보냈다. 서버에는
// "사유 미지정이면 예약 계약 줄에서 판매/구독을 파생한다"는 처리가 있었지만, 이
// 창이 유일한 수동 배치 UI 였으므로 그 파생이 **실사용 경로에서 한 번도 돌지
// 않았다** → 운영자가 드롭다운을 손대지 않으면 렌탈 계약 로봇의 인수확인서에도
// 계속 'Sale' 이 인쇄됐다.
//
// 이제 기본값이 이 빈 값이고, 빈 값이면 body 에서 reason 을 **생략**한다 —
// "내가 고르지 않았으니 계약을 읽어 정하라"는 선언이다(서버가 payload 모양을
// 보고 짐작하는 게 아니라, 화면이 뜻을 정하고 서버가 그 뜻을 읽는다).
// 운영자가 값을 고르면 그 값이 우선한다(서버도 입력값 우선).
const REASON_AUTO = '' as const;
type ReasonChoice = DeploymentReason | typeof REASON_AUTO;

export function DeployRobotModal({ open, onClose, robotId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  // 오너 보고 2026-08-24 — 이 창의 인라인 장소 등록 폼에는 역할 검사가 아예 없어서,
  // 서버가 거절할 사람에게도 폼이 보였다("버튼은 보이는데 서버가 거절"). 판정은
  // permissions.ts 의 정본 한 벌만 부른다 — 여기에 역할 리터럴을 다시 적으면
  // 서버(AbilityFactory 의 Store create)와 세 번째 사본이 생긴다.
  const authUser = useAuthStore((s) => s.user);
  const role = authUser?.role;
  const canAddPlace = role != null && canCreateStore(role);

  const [store, setStore] = useState<StoreSummaryDto | null>(null);
  // 오너 요청(2026-09-11) — 고객 배치에 **고객사**를 묻는다.
  //
  // 이 회사는 '매장' 화면을 운영에 쓰지 않아 지점을 손으로 만들 길이 없었다. 새로 낸
  // 지점은 고를 매장이 아예 없어 **배치가 막혔고**, 그래서 상태만 '운영중'으로 바꾸는
  // 사고가 났다(9/10, #245). 고친 것은 **지점을 만드는 길**이다 — 지점을 묻는 것 자체는
  // 남았다.
  //
  // 서버는 storeId 없이 customerId 만 받아도 배치한다(create-deployment.dto). 그 길은
  // **일부러 쓰지 않는다**: 그때 resolveStoreForCustomer 가 고르는 것은 ①계약라인 힌트
  // → ②고객사의 **가장 오래된** 매장 → ③고객사명으로 자동 생성이고, 지점마다 계약이
  // 따로인 이 회사에서 ②는 조용히 틀린 지점이다(모란점에 있던 로봇을 잠실새내점으로
  // 옮기면 다시 모란점으로 기록된다).
  //
  // 고객사가 하는 일은 두 가지다: 지점 검색을 그 고객사로 좁히고, 여기서 만드는
  // 지점을 그 고객사에 붙인다. 그래서 고객사를 바꾸는 **모든** 자리는 고른 지점과
  // 열린 등록 폼을 함께 비워야 한다(closePlaceForm).
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  // 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치. 로봇과 고객사의 법인이
  // 다를 수 있게 되어, 여기서 만드는 지점의 법인을 로봇이 아니라 **고객사**에서 가져와야 한다(아래
  // placeOwnerEntity). 고른 고객사의 법인을 함께 들고 있는다 — 모르면(옛 응답) null.
  const [customerEntity, setCustomerEntity] = useState<OwnerEntity | null>(null);
  /** 배정 계약에서 목적지를 자동으로 채웠는지 — 창 한 번에 한 번만 채운다. */
  const customerAutoFilledRef = useRef(false);
  // 추가수정 2차(26-07-27) §2 — '오늘' 기본값을 KST 로. UTC 기준이던 예전
  // 코드는 한국 오전 9시 전에 창을 열면 어제 날짜가 박혔다.
  const [deployedAt, setDeployedAt] = useState<string>(() => kstTodayYmd());
  const [kind, setKind] = useState<DeploymentKind>(DeploymentKind.CUSTOMER);
  // 계약 사실화 1단계 정정4(26-07-29) — 기본값은 '계약에서 자동 판정'(위 주석).
  const [reason, setReason] = useState<ReasonChoice>(REASON_AUTO);
  const [notes, setNotes] = useState<string>('');
  // 2026-10-26 오너 — "로봇 배치할때 스페어용인지 체크하는 항목도 있어야겠다". 고객 배치에만 보인다.
  const [isSpare, setIsSpare] = useState(false);
  const [eventName, setEventName] = useState<string>('');
  const [eventStart, setEventStart] = useState<string>('');
  const [eventEnd, setEventEnd] = useState<string>('');
  const [eventPurpose, setEventPurpose] = useState<string>('');
  const [eventLocation, setEventLocation] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // round11 요구4 — 전시회장처럼 매장으로 등록된 적 없는 장소에 배치하기.
  // Deployment.storeId 는 NOT NULL 이라 유형=이벤트여도 매장이 반드시 있어야
  // 하는데, 등록 화면이 영업 메뉴 안쪽이라 배치가 통째로 막혔다. 여기서 바로
  // 고객사 없는 장소를 만든다.
  const [placeFormOpen, setPlaceFormOpen] = useState(false);
  const [placeName, setPlaceName] = useState<string>('');
  const [placeAddress, setPlaceAddress] = useState<string>('');

  // round5 #19 — 예약(계약 배정)된 로봇을 이벤트/PoC/사내로 보내면 반납
  // 전까지 정식 설치 캐스케이드가 막힌다 — 차단은 하지 않되 경고로 가시화.
  const robotQ = useQuery({
    queryKey: ['robot', robotId],
    queryFn: () => robotsApi.get(robotId),
    enabled: open && !!robotId,
    staleTime: 30_000,
  });
  const reservedWarning = kind !== DeploymentKind.CUSTOMER && !!robotQ.data?.reservedContractId;

  // ── 계약 사실화 1단계 정정4(26-07-29) — 자동 판정 결과를 화면이 먼저 말한다 ──
  //
  // "저장하면 무엇이 기록되는지" 를 운영자가 모른 채 저장하게 두지 않는다. 값은
  // 로봇 조회가 이미 내려 주는 contractReason / contractReasonAmbiguous 를 쓴다 —
  // 이 두 값은 서버가 배치 때 쓰는 파생과 **같은 규칙**(로봇 모델과 일치하는 계약
  // 줄, 조건이 갈리면 판정 불가)으로 만들어지므로, 화면이 미리 보여 준 값과 실제
  // 저장되는 값이 어긋나지 않는다. 규칙을 이 화면에 다시 구현하지 않는 이유다.
  const autoReason = robotQ.data?.contractReason ?? null;
  const autoAmbiguous = robotQ.data?.contractReasonAmbiguous ?? false;
  // 판정 불가·미배정일 때 서버가 쓰는 폴백(Deployment.reason 은 비울 수 없다).
  const autoFallbackLabel = CONTRACT_REASON_OPTION_LABEL[DeploymentReason.SALE];
  // round11 요구4 안내 조건. 정정4(26-07-29) — '자동 판정'은 아직 아무 값도 고르지
  // 않은 상태이므로 '유형/사유 혼동' 이 아니다(안내 대상 아님).
  const kindReasonConfusion = reason !== REASON_AUTO && NON_CUSTOMER_REASONS.includes(reason);

  function reasonNotice(): string {
    if (reason !== REASON_AUTO) {
      const picked = CONTRACT_REASON_OPTION_LABEL[reason];
      return autoReason && autoReason !== reason
        ? `직접 고른 '${picked}' 사유로 기록됩니다. 계약을 보고 자동으로 판정한 사유는 '${CONTRACT_REASON_OPTION_LABEL[autoReason]}'입니다.`
        : `직접 고른 '${picked}' 사유로 기록됩니다.`;
    }
    if (robotQ.isPending) return '배정된 계약을 확인하는 중입니다…';
    if (!robotQ.data) {
      return `계약 정보를 확인하지 못했습니다. 이대로 저장하면 서버가 계약을 읽어 정하고, 정할 수 없으면 '${autoFallbackLabel}'로 기록됩니다.`;
    }
    if (autoReason) {
      const derived = `배정된 계약을 보고 자동으로 판정한 사유는 '${CONTRACT_REASON_OPTION_LABEL[autoReason]}'입니다.`;
      // 정정4(26-07-29) — 행사·PoC·사내 이동은 계약 조건(판매/구독)과 **다른 사건**
      // 이다. 자동 판정은 계약을 읽으므로 이 경우 계약 조건이 그대로 기록된다 —
      // 종전 기본값 SALE 도 같은 뜻으로 틀렸지만, 이제는 그 사실을 알린다.
      return kind === DeploymentKind.CUSTOMER
        ? derived
        : `${derived} 행사나 PoC, 사내 이동이라면 계약 조건과 다르니 사유를 직접 골라 주세요.`;
    }
    if (autoAmbiguous) {
      return `배정된 계약에 판매와 구독이 섞여 있어 자동으로 판정할 수 없습니다. 이대로 저장하면 '${autoFallbackLabel}'로 기록되니 맞는 사유를 직접 골라 주세요.`;
    }
    return `배정된 계약이 없어 자동 판정할 수 없습니다. 이대로 저장하면 '${autoFallbackLabel}'로 기록됩니다.`;
  }

  // 배정된 로봇이면 목적지가 이미 정해져 있다 — 그 계약의 고객사다. 로봇 조회가
  // reservedContract(고객사 id·이름)를 이미 내려 주므로 물어볼 것이 없다.
  const reservedCustomerId = robotQ.data?.reservedContract?.customerId ?? null;
  const reservedCustomerName = robotQ.data?.reservedContract?.customerDisplayName ?? null;
  // 예약 계약의 법인 = 그 고객사의 법인(서버가 계약·고객사 같은 법인을 지킨다).
  const reservedCustomerEntity = robotQ.data?.reservedContract?.ownerEntity ?? null;
  const isCustomerKind = kind === DeploymentKind.CUSTOMER;
  const robotEntity = robotQ.data?.ownerEntity ?? null;

  useEffect(() => {
    if (open) {
      // 창을 새로 열었으니 자동 채움을 한 번 더 허락한다. 아래 효과보다 **먼저**
      // 선언돼 있어야 한다 — React 는 선언 순서대로 효과를 돌리므로, 이 초기화가
      // 뒤에 있으면 열자마자 방금 채운 값을 도로 지운다.
      customerAutoFilledRef.current = false;
      setStore(null);
      setCustomerId(null);
      setCustomerName(null);
      setCustomerEntity(null);
      setDeployedAt(kstTodayYmd());
      setKind(DeploymentKind.CUSTOMER);
      // 정정4(26-07-29) — 창을 열 때마다 '자동 판정'으로 돌아간다(종전엔 SALE).
      setReason(REASON_AUTO);
      setNotes('');
      setIsSpare(false);
      setEventName('');
      setEventStart('');
      setEventEnd('');
      setEventPurpose('');
      setEventLocation('');
      setError(null);
      setPlaceFormOpen(false);
      setPlaceName('');
      setPlaceAddress('');
    }
  }, [open, robotId]);

  // 자동 채움은 창을 연 뒤 **한 번만** 돈다. "비어 있으면 채운다"로 두면 운영자가
  // [변경]을 눌러 비운 순간 같은 효과가 다시 채워 넣어 변경이 불가능해진다
  // (시험이 그 상태를 잡았다). 한 번 채웠으면 그 뒤로는 사람이 정한다.
  useEffect(() => {
    if (!open || customerAutoFilledRef.current || !reservedCustomerId) return;
    customerAutoFilledRef.current = true;
    setCustomerId(reservedCustomerId);
    setCustomerName(reservedCustomerName);
    setCustomerEntity(reservedCustomerEntity);
    // 고객사를 바꾸는 나머지 두 곳([변경]·고르개)은 고른 지점을 함께 비운다. 이
    // 자동 채움도 같아야 한다 — 배정이 늦게 도착하면(실시간 갱신으로 reservedContract
    // 가 null→값) 다른 고객사의 지점이 새 고객사 아래 남는다. 이미 맞는 지점이면
    // 건드리지 않는다.
    setStore((s) => (s && s.customerId !== reservedCustomerId ? null : s));
  }, [open, reservedCustomerId, reservedCustomerName, reservedCustomerEntity]);

  const deploy = useMutation({
    mutationFn: () =>
      deploymentsApi.create({
        robotId,
        // 목적지는 늘 매장(지점)이다. 고객 배치는 고객사도 함께 보낸다 — 서버가
        // 그 고객사가 매장과 같은 법인인지, 그 법인에 이 로봇을 쓸 수 있는지 검증하고
        // (오너 결정 2026-10-21: 비욘드허니컴 고객사 ← 허니컴로보틱스 로봇도 된다, 반대는
        // 안 된다), Deployment.customerId 를 매장에서 추측하지 않고 그대로 적는다. 배치는
        // 로봇의 소유 법인을 바꾸지 않는다 — 배치 기록은 고객사 쪽 법인으로 찍힌다.
        storeId: store?.id ?? '',
        ...(isCustomerKind && customerId ? { customerId } : {}),
        kind,
        // 정정4(26-07-29) — '자동 판정'이면 reason 을 **넣지 않는다**. 서버가
        // 예약 계약 줄에서 판매/구독을 파생한다(판정 불가면 '판매'). 값을 골랐으면
        // 그 값을 그대로 보내고 서버는 입력값을 우선한다.
        ...(reason === REASON_AUTO ? {} : { reason }),
        deployedAt: ymdToIso(deployedAt),
        ...(isCustomerKind && isSpare ? { isSpare: true } : {}),
        notes: notes || undefined,
        ...(kind === DeploymentKind.EVENT && {
          eventName: eventName || undefined,
          eventStart: eventStart ? ymdToIso(eventStart) : undefined,
          eventEnd: eventEnd ? ymdToIso(eventEnd) : undefined,
          eventPurpose: eventPurpose || undefined,
          eventLocation: eventLocation || undefined,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robotId] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      // Robot status changed (→ DEPLOYED) → lifecycle pipeline counts
      // on RobotsListPage are stale. Invalidate ALL stage count
      // queries so the pipeline reflects the move immediately rather
      // than waiting for the 60s staleTime to elapse.
      qc.invalidateQueries({ queryKey: ['robots-count'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robotId] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['deployments'] });
      toast.success('로봇이 배치되었습니다.', '배치 완료');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  // round11 요구4 — 새 장소는 고객사에 매이지 않는다(전시회장·사내 등).
  // 2026-09-11 — 고객 배치에서는 그 고객사의 **지점**으로 만든다. 고객사를 붙이지
  // 않으면 다음에 같은 고객사를 골라도 이 지점이 검색에 안 잡히고(고객사로 좁히므로),
  // 서버의 매장 자동 해소도 이 지점을 후보로 보지 못한다.
  //
  // 지점·장소의 법인 — 오너 결정 2026-10-21(허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치).
  // 종전엔 로봇의 법인으로 만들었다(로봇 = 매장 법인이 규칙이었다). 이제 그러면 직원이
  // 허니컴로보틱스 로봇을 배치하며 지점을 만들 때 '허니컴로보틱스 지점'을 만들려다 서버가
  // 거절하고(직원은 자기 법인 매장만 만든다), 고객사(비욘드허니컴)와 법인이 갈린다. 그래서:
  //   ① 고객 배치 — 고른 고객사의 법인(서버: 고객사 = 매장 법인).
  //   ② 그 밖(행사·PoC·사내) — 내 법인에 이 로봇을 쓸 수 있으면 내 법인, 아니면 로봇의 법인.
  // 어느 쪽이든 그 지점에 이 로봇을 쓸 수 있는지는 서버가 다시 본다.
  function placeOwnerEntity(robotOwner: OwnerEntity): OwnerEntity {
    if (isCustomerKind && customerEntity) return customerEntity;
    if (authUser && robotUsableAt(robotOwner, authUser.ownerEntity)) return authUser.ownerEntity;
    return robotOwner;
  }
  const createPlace = useMutation({
    mutationFn: () =>
      storesApi.create({
        name: placeName.trim(),
        addressLine1: placeAddress.trim() || undefined,
        ownerEntity: placeOwnerEntity(robotQ.data!.ownerEntity),
        ...(isCustomerKind && customerId ? { customerId } : {}),
      }),
    onSuccess: (created) => {
      setPlaceFormOpen(false);
      setPlaceName('');
      setPlaceAddress('');
      // 등록 요청이 오가는 사이에 고객사를 바꿨을 수 있다. 그때 만들어진 지점은
      // **앞 고객사의** 것이므로 자동 선택하면 A 의 지점이 B 의 목적지로 실린다
      // (서버는 매장과 고객사가 같은 고객사인지 보지 않는다 — 법인만 본다).
      // 고르지 않되 조용히 넘기지 않는다: 무엇을 만들었고 왜 안 골랐는지 말한다.
      const madeFor = created.customerId ?? null;
      if (isCustomerKind && madeFor !== customerId) {
        toast.success(
          `'${created.name}' 지점을 등록했지만, 그 사이 고객사가 바뀌어 선택하지는 않았습니다. 필요하면 다시 골라 주세요.`,
          '지점 등록 완료',
        );
        return;
      }
      setStore(created);
      toast.success(
        isCustomerKind ? '지점이 등록되어 선택되었습니다.' : '장소가 등록되어 선택되었습니다.',
        isCustomerKind ? '지점 등록 완료' : '장소 등록 완료',
      );
    },
    onError: (err) => setError(extractApiError(err)),
  });

  /**
   * 지점·장소 등록 폼을 연다.
   *
   * 오너 화면 2026-09-11 — 이 회사의 고객사 이름에 **지점이 이미 들어 있다**
   * (예: '1989 삼칠갈비 잠실새내점'). 그런 고객사에서 지점 이름을 처음부터 다시
   * 타이핑하게 하는 것은 같은 말을 두 번 적으라는 뜻이다. 고객사 이름을 미리 채워
   * 두고, 지점을 따로 쓰는 고객사('삼칠갈비' + '모란점')면 고쳐 쓰게 한다.
   * 자동으로 만들지 않고 **보여 주고 고치게** 하는 이유: 무엇이 만들어지는지
   * 저장 전에 눈으로 확인해야 엉뚱한 이름의 지점이 조용히 생기지 않는다.
   */
  function openPlaceForm(): void {
    setPlaceFormOpen(true);
    if (isCustomerKind && !placeName && customerName) setPlaceName(customerName);
  }

  /** 지점·장소 등록 폼을 접고 입력값을 버린다. 목적지가 바뀌는 모든 자리에서 부른다. */
  function closePlaceForm(): void {
    setPlaceFormOpen(false);
    setPlaceName('');
    setPlaceAddress('');
  }

  function handleKindChange(newKind: DeploymentKind): void {
    setKind(newKind);
    if (newKind !== DeploymentKind.EVENT) {
      setEventName('');
      setEventStart('');
      setEventEnd('');
      setEventPurpose('');
      setEventLocation('');
    }
    // 고객 배치와 그 밖의 유형은 고르는 대상이 다르다(고객사 지점 ↔ 아무 장소).
    // 경계를 넘을 때 고른 값을 그대로 두면, 전시회장을 고른 채 고객 배치로 돌아가
    // 고객사 없는 장소로 저장되거나 그 반대가 된다 — 넘을 때만 비운다.
    if ((newKind === DeploymentKind.CUSTOMER) !== (kind === DeploymentKind.CUSTOMER)) {
      setStore(null);
      closePlaceForm();
    }
  }

  function submitPlace(): void {
    setError(null);
    // 고객 배치의 등록은 **누구의 지점인지**가 정해져 있어야 한다. 고객사가 없으면
    // 고객사 없는 장소가 만들어지고, 그 지점은 고객사로 좁힌 검색에 영영 안 잡혀
    // 운영자가 같은 이름을 또 만들게 된다. 폼을 숨기는 것(아래 렌더 조건)만으로는
    // 부족해서 여기서도 막는다 — 창은 폼을 열어 둔 채 고객사만 비울 수 있다.
    if (isCustomerKind && !customerId) {
      setError('고객사를 먼저 선택해 주세요. 지점은 고객사에 속합니다.');
      return;
    }
    if (!placeName.trim()) {
      setError(isCustomerKind ? '지점 이름을 입력해 주세요.' : '장소 이름을 입력해 주세요.');
      return;
    }
    if (!robotQ.data) {
      setError('로봇 정보를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    createPlace.mutate();
  }

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    // 고객 배치의 목적지는 **지점**이다. 고객사만 받고 매장을 서버에 맡기면
    // resolveStoreForCustomer 가 '그 고객사의 가장 오래된 매장'을 고른다 —
    // 지점이 여럿인 고객사(지점마다 계약이 따로다)에서는 조용히 엉뚱한 지점에
    // 들어간다. 묻는 것을 없애는 대신, 바로 위에서 지점을 만들 수 있게 했다.
    if (isCustomerKind && !customerId) {
      setError('고객사를 선택해 주세요.');
      return;
    }
    if (!store) {
      setError(
        isCustomerKind
          ? '지점을 선택하거나 새로 등록해 주세요.'
          : '장소를 선택하거나 새로 등록해 주세요.',
      );
      return;
    }
    // 오너 결정 2026-10-21 — 비욘드허니컴 고객사·매장에는 허니컴로보틱스 로봇도 배치되지만, 반대
    // (허니컴로보틱스 고객사·매장 ← 비욘드허니컴 로봇)는 안 된다. 서버가 막는 조합을 저장 전에
    // 서버와 같은 말로 알린다(관리자만 두 법인 매장을 고를 수 있어 실제로 걸리는 사람은 관리자뿐).
    // 법인을 모르면(옛 응답) 서버에 맡긴다.
    if (robotEntity && store.ownerEntity && !robotUsableAt(robotEntity, store.ownerEntity)) {
      setError(robotUseDeniedMessage(robotEntity, store.ownerEntity));
      return;
    }
    if (kind === DeploymentKind.EVENT && !eventName) {
      setError('이벤트명을 입력해 주세요.');
      return;
    }
    if (kind === DeploymentKind.EVENT) {
      if (!eventStart) {
        setError('이벤트 시작일을 입력해 주세요.');
        return;
      }
      if (eventEnd && new Date(eventStart) > new Date(eventEnd)) {
        setError('이벤트 시작일이 종료일보다 늦습니다.');
        return;
      }
    }
    deploy.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="로봇 배치"
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="deploy-form" disabled={deploy.isPending}>
            {deploy.isPending ? '처리 중…' : '배치'}
          </Button>
        </>
      }
    >
      <form id="deploy-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        {/* 고객 배치 — 목적지는 고객사. 배정된 로봇이면 이미 채워져 있다. */}
        {kind === DeploymentKind.CUSTOMER && (
          <Field label="고객사" required>
            {/* 이름과 [변경]은 한 줄에, 출처는 그 아래 줄로 내린다. 종전엔 셋을 한
                줄에 두어 고객사 이름이 길면(예: '1989 삼칠갈비 잠실새내점') 출처
                문장이 [변경] 링크를 감싸며 줄바꿈돼 읽을 수 없었다 — 오너 화면
                2026-09-11. */}
            {customerId && customerName ? (
              <div style={pickedCustomer} data-testid="dep-customer-picked">
                <div style={pickedCustomerRow}>
                  <b>{customerName}</b>
                  <button
                    type="button"
                    data-testid="dep-customer-change"
                    onClick={() => {
                      setCustomerId(null);
                      setCustomerName(null);
                      setCustomerEntity(null);
                      setStore(null);
                      // 열어 둔 지점 등록 폼도 함께 닫는다 — 누구의 지점인지 모르는
                      // 채로 남겨 두면 고객사 없는 지점이 만들어진다.
                      closePlaceForm();
                    }}
                    style={linkButton}
                  >
                    변경
                  </button>
                </div>
                {reservedCustomerId === customerId && robotQ.data?.reservedContract?.humanId && (
                  <div style={{ marginTop: 4, fontSize: 12, color: 'var(--fg-muted)' }}>
                    배정된 계약 {robotQ.data.reservedContract.humanId} 에서 가져왔습니다
                  </div>
                )}
              </div>
            ) : (
              <CustomerPicker
                selected={customerId}
                onSelect={(c) => {
                  setCustomerId(c.id);
                  setCustomerName(c.displayName);
                  setCustomerEntity(c.ownerEntity ?? null);
                  setStore(null);
                  // 앞 고객사를 위해 열어 둔 등록 폼은 닫는다(입력해 둔 이름이
                  // 새 고객사의 지점으로 둔갑하지 않게).
                  closePlaceForm();
                }}
                // 오너 결정 2026-10-21 — 이 로봇을 받을 수 있는 법인의 고객사만. 직원은 자기
                // 법인(그 법인에는 볼 수 있는 로봇이 다 간다), 관리자는 비욘드허니컴 로봇이면
                // 비욘드허니컴 고객사로 좁히고 허니컴로보틱스 로봇이면 좁히지 않는다.
                ownerEntity={customerPickerEntityForRobot(authUser, robotEntity)}
                placeholder="고객사 검색"
              />
            )}

            {/* 검토 지적(2026-09-11) — 종전 '배정 가능 고객사만' 체크상자(기본 켜짐)가
                계약이 살아 있는 고객사의 매장만 보여 주었다. 목적지가 고객사로 바뀌면서
                그 체크상자는 없어졌다. 같은 것을 되살리려면 고객사의 계약 상태가
                필요한데 고객사 목록 응답에 그 칸이 없다(서버 변경 — 이번 차수 밖).
                대신 **이 창이 이미 아는 사실**로 말한다: 배정된 계약의 고객사와 다른
                곳을 고르면 그 사실을 알린다. 계약 상태 확인까지는 아니므로 그렇게
                말하지 않는다 — 없는 확신을 주지 않기 위해서다. */}
            {customerId && reservedCustomerId && customerId !== reservedCustomerId && (
              <div style={destinationWarn} data-testid="dep-customer-not-reserved">
                배정된 계약 {robotQ.data?.reservedContract?.humanId ?? '—'}의 고객사가 아닙니다.
                맞는 곳인지 확인해 주세요.
              </div>
            )}
            {customerId && !reservedCustomerId && (
              <div style={destinationWarn} data-testid="dep-customer-unassigned">
                이 로봇은 배정된 계약이 없어 고객사를 대조할 수 없습니다. 계약이 살아 있는
                고객사인지 확인해 주세요.
              </div>
            )}
          </Field>
        )}

        <Field label={isCustomerKind ? '지점' : '장소'} required>
          {/* StorePicker uses server-side search (pageSize=20) so tenants with
              >200 stores can still deploy to any store. 고객 배치는 그 고객사의
              지점으로 좁힌다 — 좁히지 않으면 이름이 비슷한 남의 매장이 검색에
              섞여 로봇이 다른 회사로 들어간다. 그 밖의 유형(행사·PoC·사내)은
              장소 이름으로 바로 찾는 편이 맞으므로 종전대로 전체를 본다. */}
          {/* 고객 배치에서는 칸 이름이 '지점'이니 안내 문구도 지점으로 맞춘다. 기본
              문구('상호 / 매장명 / 지점 검색…')는 전체 매장을 훑는 다른 유형용이다 —
              한 칸을 세 이름으로 부르면 같은 것인지 운영자가 알 수 없다. */}
          <StorePicker
            value={store}
            onChange={setStore}
            customerId={isCustomerKind ? customerId : null}
            {...(isCustomerKind ? { placeholder: '지점 이름으로 검색…' } : {})}
          />

          {/* 2026-09-10 에 넣었던 '매장 화면에서 먼저 등록하세요' 안내는 지웠다 —
              팀은 매장 화면을 쓰지 않아 그 길이 실제로는 없었다(오너 9/11).
              대신 아래 등록 폼을 고객 배치에서도 연다. */}
          {isCustomerKind && !customerId && (
            <div
              data-testid="dep-store-needs-customer"
              style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6 }}
            >
              고객사를 먼저 고르면 그 고객사의 지점만 보여 줍니다.
            </div>
          )}

          {/* round11 요구4 — 전시회장·행사장은 매장으로 등록된 적이 없어 검색해도
              안 나온다. 영업 메뉴로 나가지 않고 여기서 바로 만든다.
              2026-09-11 — 고객사 지점도 같은 처지였다(매장 화면 미사용). 고객사를
              고른 뒤에는 지점도 여기서 만든다. */}
          {canAddPlace && !placeFormOpen && (!isCustomerKind || customerId) && (
            <button
              type="button"
              data-testid="dep-new-place-open"
              onClick={() => openPlaceForm()}
              style={{
                marginTop: 6,
                padding: 0,
                background: 'none',
                border: 'none',
                fontSize: 12,
                color: 'var(--brand)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {isCustomerKind ? '+ 목록에 없는 지점 등록' : '+ 전시회장처럼 목록에 없는 장소 등록'}
            </button>
          )}

          {/* 오너 보고 2026-08-24 — 폼을 그냥 숨기면 "검색해도 안 나오는데 만들 수도
              없다"에서 사용자가 막힌다. 무엇을 하면 되는지 그 자리에서 말한다. */}
          {!canAddPlace && (!isCustomerKind || customerId) && (
            <div
              data-testid="dep-new-place-denied"
              style={{
                marginTop: 6,
                fontSize: 12,
                lineHeight: 1.5,
                color: 'var(--fg-muted)',
              }}
            >
              {isCustomerKind
                ? '지점을 새로 등록할 권한이 없습니다. 검색해도 나오지 않는 지점은 영업팀에 매장 등록을 요청해 주세요.'
                : '장소를 새로 등록할 권한이 없습니다. 전시회장이나 공장처럼 검색해도 나오지 않는 장소는 영업팀에 매장 등록을 요청해 주세요.'}
            </div>
          )}

          {/* 열기 버튼·권한 안내와 **같은 조건**을 건다. 종전엔 이 줄만 조건이 달라서,
              폼을 연 뒤 [변경]으로 고객사를 비우면 폼이 살아남아 고객사 없는 지점이
              만들어졌다(검토에서 실제 재현됨). */}
          {canAddPlace && placeFormOpen && (!isCustomerKind || customerId) && (
            <div
              data-testid="dep-new-place-form"
              style={{
                marginTop: 8,
                padding: 10,
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-subtle, var(--bg-surface))',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{ fontSize: 12, color: 'var(--fg-muted)' }}
                data-testid="dep-new-place-owner"
              >
                {isCustomerKind && customerName
                  ? `${customerName}의 지점으로 등록됩니다. 등록하면 곧바로 선택됩니다.`
                  : '고객사 없는 장소로 등록됩니다. 등록하면 곧바로 선택됩니다.'}
              </div>
              <Field
                label={isCustomerKind ? '지점 이름' : '장소 이름'}
                htmlFor="dep-place-name"
                required
              >
                <Input
                  id="dep-place-name"
                  value={placeName}
                  onChange={(e) => setPlaceName(e.target.value)}
                  placeholder={isCustomerKind ? '예: 잠실새내점' : '예: 코엑스 A홀'}
                />
              </Field>
              <Field label="주소" htmlFor="dep-place-address">
                <Input
                  id="dep-place-address"
                  value={placeAddress}
                  onChange={(e) => setPlaceAddress(e.target.value)}
                />
              </Field>
              <div style={{ display: 'flex', gap: 6 }}>
                {/* 배치 폼(form=deploy-form) 안이라 type="button" 필수 —
                    Enter/클릭이 배치 제출로 새지 않게 한다. */}
                <Button
                  type="button"
                  onClick={submitPlace}
                  disabled={createPlace.isPending || !robotQ.data}
                >
                  {createPlace.isPending ? '등록 중…' : isCustomerKind ? '지점 등록' : '장소 등록'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setPlaceFormOpen(false)}>
                  취소
                </Button>
              </div>
            </div>
          )}
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* 추가수정 2차(26-07-27) §2 — 공휴일 경고 확산(결정 ③A). 브라우저
              기본 날짜 팝업은 색칠이 불가능하므로 InfrastructureOrderModal 과
              같은 방식으로 입력칸 아래 문구를 띄운다. */}
          <Field
            label="배치 일자"
            htmlFor="dep-date"
            required
            hint={
              isKoreanHoliday(deployedAt)
                ? `공휴일 (${getHolidayName(deployedAt)}) 입니다`
                : undefined
            }
          >
            <Input
              id="dep-date"
              type="date"
              value={deployedAt}
              onChange={(e) => setDeployedAt(e.target.value)}
              required
            />
          </Field>
          <Field label="유형" htmlFor="dep-kind">
            <Select
              id="dep-kind"
              value={kind}
              onChange={(e) => handleKindChange(e.target.value as DeploymentKind)}
            >
              <option value={DeploymentKind.CUSTOMER}>
                {DEPLOYMENT_KIND_LABELS[DeploymentKind.CUSTOMER]}
              </option>
              <option value={DeploymentKind.EVENT}>
                {DEPLOYMENT_KIND_LABELS[DeploymentKind.EVENT]}
              </option>
              <option value={DeploymentKind.PILOT}>
                {DEPLOYMENT_KIND_LABELS[DeploymentKind.PILOT]}
              </option>
              {/* 오너 요청 2026-09-18 — 인증·시험과 테스트를 파일럿과 분리.
                  섞어 두면 "인증기관에 나가 있는 로봇이 몇 대인가" 에 답할 수 없다. */}
              <option value={DeploymentKind.CERTIFICATION}>
                {DEPLOYMENT_KIND_LABELS[DeploymentKind.CERTIFICATION]}
              </option>
              <option value={DeploymentKind.TEST}>
                {DEPLOYMENT_KIND_LABELS[DeploymentKind.TEST]}
              </option>
              <option value={DeploymentKind.INTERNAL}>
                {DEPLOYMENT_KIND_LABELS[DeploymentKind.INTERNAL]}
              </option>
            </Select>
            {/* 오너 결정 2026-09-18 — 고른 종류가 **로봇 상태를 정한다.** 저장하기
                전에 그 결과를 그 자리에서 보여 준다. 종전엔 무엇을 고르든 '운영중'
                이 됐고, 왜 대시보드 숫자와 다른지 아무도 알 수 없었다. */}
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--fg-muted)' }}>
              {/* 조사(이/가)가 상태 이름마다 달라('운영중이'·'기타가') 문장을 콜론으로 끊는다
                  — 종전 '… 기타 이 됩니다' 는 틀린 조사였다(2026-09-23). */}
              전시회나 데모라면 &lsquo;행사·전시&rsquo;를 고르세요. 배치 후 로봇 상태:{' '}
              <strong data-testid="dep-kind-resulting-status" style={{ color: 'var(--fg)' }}>
                {ROBOT_STATUS_LABELS[robotStatusForDeploymentKind(kind)]}
              </strong>
              {kind === DeploymentKind.CUSTOMER
                ? ''
                : '. 고객사 실서비스가 아니어서 운영중 대수에 넣지 않습니다.'}
            </div>
          </Field>
        </div>

        {/* round11 요구4 — 전시회 배치가 막힌 원인은 '유형'이 아니라 '사유'를
            이벤트로 고른 것이었다. 사유만 바꾸면 매장 검색이 여전히 배정 가능
            고객사로 좁혀져 전시회장이 안 나온다. 둘의 차이를 그 자리에서 알린다. */}
        {kind === DeploymentKind.CUSTOMER && kindReasonConfusion && (
          <div
            role="alert"
            data-testid="dep-kind-reason-hint"
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              padding: '8px 10px',
              borderRadius: 'var(--r-btn)',
              border: '1px solid var(--border-default)',
              color: 'var(--fg-muted)',
            }}
          >
            &lsquo;사유&rsquo;만 바꿔서는 고객사 매장 중에서만 고를 수 있습니다. 전시회장처럼 고객사
            없는 장소에 놓으려면 위의 &lsquo;유형&rsquo;을 바꾸세요.{' '}
            <button
              type="button"
              data-testid="dep-kind-reason-hint-switch"
              onClick={() => handleKindChange(DeploymentKind.EVENT)}
              style={{
                padding: 0,
                background: 'none',
                border: 'none',
                color: 'var(--brand)',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: 12,
              }}
            >
              유형을 &lsquo;행사·전시&rsquo;로 바꾸기
            </button>
          </div>
        )}

        {reservedWarning && (
          <div
            role="alert"
            data-testid="dep-reserved-warning"
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              padding: '8px 10px',
              borderRadius: 'var(--r-btn)',
              border: '1px solid var(--dday-warning)',
              background: 'var(--bg-warning-subtle, var(--bg-accent-subtle))',
              color: 'var(--fg-default)',
            }}
          >
            이 로봇은 계약에 배정되어 있습니다. 임시로 옮겨 둔 동안에는 그 계약의 설치 완료 처리가
            되지 않으니, 반납 일정을 계약 설치 일정보다 앞서 잡아 주세요. 반납해도 계약 배정은
            그대로 남습니다.
          </div>
        )}

        {kind === DeploymentKind.EVENT && (
          <>
            <Field label="이벤트명" htmlFor="dep-event-name" required>
              <Input
                id="dep-event-name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="시작일" htmlFor="dep-event-start" required>
                <Input
                  id="dep-event-start"
                  type="date"
                  value={eventStart}
                  onChange={(e) => setEventStart(e.target.value)}
                />
              </Field>
              <Field label="종료일" htmlFor="dep-event-end">
                <Input
                  id="dep-event-end"
                  type="date"
                  value={eventEnd}
                  onChange={(e) => setEventEnd(e.target.value)}
                />
              </Field>
            </div>
            <Field label="목적" htmlFor="dep-event-purpose">
              <Input
                id="dep-event-purpose"
                value={eventPurpose}
                onChange={(e) => setEventPurpose(e.target.value)}
              />
            </Field>
            <Field label="장소" htmlFor="dep-event-location">
              <Input
                id="dep-event-location"
                value={eventLocation}
                onChange={(e) => setEventLocation(e.target.value)}
              />
            </Field>
          </>
        )}

        {/* 계약 사실화 1단계 정정3(26-07-28) — 여기 있던 **네 번째 라벨 세트**
            ('운용 리스'/'금융 리스'/'데모'/'이벤트'/'내부')를 지웠다. 이 드롭다운이
            고른 값이 곧 Deployment.reason 이고 그것이 인수확인서 'Reason' 과 로봇
            목록 '구분' 으로 나가므로, 여기서 '운용 리스'로 배치한 로봇을 로봇
            목록은 '구독'이라 불러 같은 배치를 두 화면이 다른 말로 불렀다.
            이제 표기 정본(lib/contract-reason)의 긴 표기를 그대로 쓴다 —
            계약 등록 마법사 '배치 사유' 와 같은 세트·같은 순서.
            값(enum)은 하나도 바뀌지 않으므로 저장되는 내용은 종전과 같다.

            계약 사실화 1단계 정정4(26-07-29) — 여기에 맨 앞 항목 '계약에서 자동
            판정'(빈 값)이 붙고 그것이 **기본값**이 됐다. 정정3 까지도 이 창은 늘
            SALE 을 보내고 있어서, "미지정이면 계약 줄에서 파생"이라는 서버 처리가
            실사용 경로에서 한 번도 돌지 않았다 — 렌탈 계약 로봇의 인수확인서에
            'Sale' 이 계속 인쇄된 진짜 원인. 이제 저장되는 값은 화면이 먼저 말한다. */}
        <Field label="사유" htmlFor="dep-reason">
          <Select
            id="dep-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as ReasonChoice)}
          >
            {/* 계약 사실화 1단계 정정4(26-07-29) — 첫 항목이 기본값. 고르지 않으면
                서버가 배정된 계약의 줄에서 판매/구독을 판정한다. 무엇으로 기록될지는
                아래 문구가 말한다(dep-reason-auto). */}
            <option value={REASON_AUTO}>계약에서 자동 판정</option>
            {Object.entries(CONTRACT_REASON_OPTION_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          {/* 정정4(26-07-29) — 저장될 값을 그 자리에서 알린다. 종전엔 기본값 '판매'가
              말없이 저장돼, 렌탈 계약 로봇의 인수확인서에 'Sale' 이 인쇄됐다. */}
          <div
            data-testid="dep-reason-auto"
            style={{ marginTop: 4, fontSize: 11, color: 'var(--fg-default)' }}
          >
            {reasonNotice()}
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--fg-muted)' }}>
            사유는 기록용이며, 고를 수 있는 {isCustomerKind ? '지점' : '장소'} 범위에는 영향을 주지
            않습니다.
          </div>
        </Field>

        {isCustomerKind && (
          <label style={spareRow}>
            <input
              type="checkbox"
              data-testid="dep-spare"
              checked={isSpare}
              onChange={(e) => setIsSpare(e.target.checked)}
            />
            <span>
              스페어 로봇으로 지급
              <span style={{ display: 'block', fontSize: 11, color: 'var(--fg-muted)' }}>
                계약 대수와 별도로 매장에 두는 로봇입니다. 설치 대수에 포함되지 않고, 계약에
                자동으로 배정되지 않습니다.
              </span>
            </span>
          </label>
        )}

        <Field label="메모" htmlFor="dep-notes">
          <Input id="dep-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

const spareRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  margin: '4px 0 12px',
  fontSize: 13,
  cursor: 'pointer',
};

/** 고객사가 이미 정해졌을 때의 표시 줄 — 고른 곳과 '변경'을 한 줄에. */
// 이름 + [변경] 한 줄. 이름이 길어도 링크가 문장에 끼어들지 않게 둘만 둔다.
const pickedCustomerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
};

// 바깥 상자는 **세로로 쌓는다**(이름줄 → 출처 문장). 가로 배치로 두면 두 덩이가
// 나란히 서서 출처 문장이 오른쪽으로 잘린다 — 2026-09-11 에 안쪽 줄(pickedCustomerRow)
// 만 만들고 이 줄을 안 고쳐서 화면이 그대로였다(오너 재신고). 구조만 보는 시험은
// 그 상태를 통과시켰으므로, 이제 배치 방향 자체를 시험이 본다.
const pickedCustomer: CSSProperties = {
  padding: '9px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-subtle, var(--bg-surface))',
  fontSize: 14,
};

const linkButton: CSSProperties = {
  padding: 0,
  background: 'none',
  border: 'none',
  fontSize: 12,
  color: 'var(--brand)',
  cursor: 'pointer',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

// 목적지를 다시 보라는 알림. 막는 것이 아니라 알리는 것이므로 경고색까지는 쓰지
// 않는다 — 정당한 이동(추가 판매·재계약 전 선배치)도 여기 걸린다.
const destinationWarn: CSSProperties = {
  marginTop: 6,
  padding: '7px 10px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-warning, var(--border-default))',
  background: 'var(--bg-warning-subtle, var(--bg-subtle, var(--bg-surface)))',
  fontSize: 12,
  lineHeight: 1.6,
};
